import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "@/lib/api";
import { money } from "@/lib/format";
import { Alert, Badge, Button, Card, Field, PageHeader, Skeleton, Table } from "@/components/ui";

/**
 * The margin side of pricing.
 *
 * Service levels decide what a route costs to run: a base fee, a rate per
 * kilometre, a minimum. A rate card decides what the customer pays on top of
 * that, and until this screen existed it could only be changed with a migration
 * — which is why removing VAT needed one.
 *
 * Nothing here is ever shown to a customer. A quote returns a price; how that
 * price was arrived at is WeShip's own business.
 */

interface RateCard {
  id: string;
  name: string;
  markup_percent: number;
  markup_fixed_cents: number;
  min_margin_cents: number;
  insurance_rate: number;
  fuel_levy_percent: number;
  vat_percent: number;
  is_default: boolean;
  /** How many accounts price off this card right now. */
  accounts: number;
}

/** The form holds strings, because a half-typed number is not a number. */
interface Draft {
  name: string;
  markup_percent: string;
  markup_fixed: string;
  min_margin: string;
  insurance_percent: string;
  fuel_levy_percent: string;
  vat_percent: string;
}

const EMPTY: Draft = {
  name: "",
  markup_percent: "",
  markup_fixed: "",
  min_margin: "",
  insurance_percent: "1.5",
  fuel_levy_percent: "0",
  vat_percent: "0",
};

function toDraft(card: RateCard): Draft {
  return {
    name: card.name,
    markup_percent: String(card.markup_percent),
    markup_fixed: (card.markup_fixed_cents / 100).toString(),
    min_margin: (card.min_margin_cents / 100).toString(),
    // Stored as a fraction of declared value, shown as a percentage: 0.015 is
    // the kind of number that gets typed with the decimal point in the wrong
    // place.
    insurance_percent: (card.insurance_rate * 100).toString(),
    fuel_levy_percent: String(card.fuel_levy_percent),
    vat_percent: String(card.vat_percent),
  };
}

function toPayload(draft: Draft) {
  const rands = (value: string) => Math.round((Number(value) || 0) * 100);
  return {
    name: draft.name.trim(),
    markup_percent: Number(draft.markup_percent) || 0,
    markup_fixed_cents: rands(draft.markup_fixed),
    min_margin_cents: rands(draft.min_margin),
    insurance_rate: (Number(draft.insurance_percent) || 0) / 100,
    fuel_levy_percent: Number(draft.fuel_levy_percent) || 0,
    vat_percent: Number(draft.vat_percent) || 0,
  };
}

export function RateCards() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>({ ...EMPTY });
  const [error, setError] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["rate-cards"],
    queryFn: () => api.get<{ data: RateCard[] }>("/v1/admin/rates/cards"),
  });
  const cards = data?.data ?? [];

  const done = () => {
    setEditing(null);
    setDraft({ ...EMPTY });
    setError("");
    queryClient.invalidateQueries({ queryKey: ["rate-cards"] });
  };
  const failed = (err: unknown) =>
    setError(err instanceof ApiError ? err.message : "That did not save. Try again.");

  const save = useMutation({
    mutationFn: () =>
      editing === "new"
        ? api.post<RateCard>("/v1/admin/rates/cards", toPayload(draft))
        : api.put<RateCard>(`/v1/admin/rates/cards/${editing}`, toPayload(draft)),
    onSuccess: done,
    onError: failed,
  });

  const makeDefault = useMutation({
    mutationFn: (id: string) => api.post(`/v1/admin/rates/cards/${id}/default`, {}),
    onSuccess: done,
    onError: failed,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.remove(`/v1/admin/rates/cards/${id}`),
    onSuccess: done,
    onError: failed,
  });

  return (
    <>
      <PageHeader
        title="Rate cards"
        subtitle="What a customer pays on top of what a shipment costs us. Never shown to them."
      />

      {error && (
        <div className="mb-4">
          <Alert>{error}</Alert>
        </div>
      )}

      {editing === null && (
        <div className="mb-4">
          <Button
            onClick={() => {
              setEditing("new");
              setDraft({ ...EMPTY });
            }}
          >
            New rate card
          </Button>
        </div>
      )}

      {editing !== null && (
        <Card className="mb-5">
          <h2 className="text-lg font-bold tracking-tight">
            {editing === "new" ? "New rate card" : "Edit rate card"}
          </h2>
          <p className="mt-1 text-sm text-ink-500">
            Markup is whichever of the two is larger, never the sum, and the result is lifted to the
            margin floor. A percentage protects the margin on an expensive shipment; a fixed amount
            protects it on a cheap one.
          </p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field
              label="Name"
              value={draft.name}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              placeholder="Standard"
            />
            <Field
              label="Markup %"
              value={draft.markup_percent}
              onChange={(event) => setDraft({ ...draft, markup_percent: event.target.value })}
              hint="Of what the shipment costs us"
            />
            <Field
              label="Markup, fixed (R)"
              value={draft.markup_fixed}
              onChange={(event) => setDraft({ ...draft, markup_fixed: event.target.value })}
              hint="Per shipment"
            />
            <Field
              label="Margin floor (R)"
              value={draft.min_margin}
              onChange={(event) => setDraft({ ...draft, min_margin: event.target.value })}
              hint="Never book for less gross margin than this"
            />
            <Field
              label="Insurance %"
              value={draft.insurance_percent}
              onChange={(event) => setDraft({ ...draft, insurance_percent: event.target.value })}
              hint="Of declared value, when insurance is taken"
            />
            <Field
              label="Fuel levy %"
              value={draft.fuel_levy_percent}
              onChange={(event) => setDraft({ ...draft, fuel_levy_percent: event.target.value })}
              hint="On the cost, before markup"
            />
            <Field
              label="VAT %"
              value={draft.vat_percent}
              onChange={(event) => setDraft({ ...draft, vat_percent: event.target.value })}
              hint="Zero: this business is not VAT registered"
            />
          </div>
          <div className="mt-4 flex gap-2">
            <Button
              loading={save.isPending}
              disabled={!draft.name.trim()}
              onClick={() => save.mutate()}
            >
              Save
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setEditing(null);
                setError("");
              }}
            >
              Cancel
            </Button>
          </div>
        </Card>
      )}

      {isLoading ? (
        <Card>
          <Skeleton className="h-48 w-full" />
        </Card>
      ) : cards.length === 0 ? (
        <Card>
          <p className="text-sm text-ink-500">No rate cards yet.</p>
        </Card>
      ) : (
        <Table head={["Card", "Markup", "Floor", "Insurance", "Levy", "VAT", "Accounts", ""]}>
          {cards.map((card) => (
            <tr key={card.id}>
              <td className="ws-td">
                <span className="font-semibold">{card.name}</span>
                {card.is_default && (
                  <span className="ml-2">
                    <Badge tone="good">default</Badge>
                  </span>
                )}
                <p className="mt-0.5 font-mono text-xs text-ink-400">{card.id}</p>
              </td>
              <td className="ws-td text-ink-600">
                {card.markup_percent}% or {money(card.markup_fixed_cents)}
              </td>
              <td className="ws-td text-ink-600">{money(card.min_margin_cents)}</td>
              <td className="ws-td text-ink-600">{(card.insurance_rate * 100).toFixed(2)}%</td>
              <td className="ws-td text-ink-600">{card.fuel_levy_percent}%</td>
              <td className="ws-td text-ink-600">{card.vat_percent}%</td>
              <td className="ws-td text-ink-600">{card.accounts}</td>
              <td className="ws-td">
                <div className="flex flex-wrap justify-end gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setEditing(card.id);
                      setDraft(toDraft(card));
                      setError("");
                    }}
                  >
                    Edit
                  </Button>
                  {!card.is_default && (
                    <Button
                      variant="secondary"
                      loading={makeDefault.isPending}
                      onClick={() => makeDefault.mutate(card.id)}
                    >
                      Make default
                    </Button>
                  )}
                  {/* Deleting a card accounts are on would leave them pricing
                      at the engine's built-in default instead of what they were
                      sold, so the button is not offered when it is in use. The
                      API refuses it regardless. */}
                  {!card.is_default && card.accounts === 0 && (
                    <Button
                      variant="secondary"
                      loading={remove.isPending}
                      onClick={() => remove.mutate(card.id)}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </>
  );
}
